/**
 * Auto-removes the Wide Awake free gift if cart has fewer than 2 items
 * from the eligible collection.
 */
const INITIATOR = 'cart-wide-awake-gwp';

export class CartWideAwakeGwp extends HTMLElement {
  constructor() {
    super();
    this.onCartUpdate = this.onCartUpdate.bind(this);
    this.onCartOpen = this.onCartOpen.bind(this);
    this.onCartRequestStart = this.onCartRequestStart.bind(this);
    this.processing = false;
    // Bumped whenever a theme cart mutation starts. A /cart.js snapshot taken
    // before the bump is stale and must not drive a removal; the request-end
    // handler re-checks against the fresh post-mutation cart instead.
    this.mutationToken = 0;
  }

  connectedCallback() {
    document.addEventListener('liquid-ajax-cart:request-start', this.onCartRequestStart);
    document.addEventListener('liquid-ajax-cart:request-end', this.onCartUpdate);
    window.addEventListener('cart-open', this.onCartOpen);
    this.initialCheck();
  }

  disconnectedCallback() {
    document.removeEventListener('liquid-ajax-cart:request-start', this.onCartRequestStart);
    document.removeEventListener('liquid-ajax-cart:request-end', this.onCartUpdate);
    window.removeEventListener('cart-open', this.onCartOpen);
  }

  onCartRequestStart() {
    this.mutationToken += 1;
  }

  onCartOpen() {
    this.fetchCartAndCheck();
  }

  onCartUpdate(event) {
    const { requestState } = event.detail || {};
    if (!requestState?.responseData) return;

    // Ignore self-initiated removal requests to avoid infinite loops
    if (requestState.info?.initiator === INITIATOR) return;
    if (!requestState.responseData.ok) return;

    const cart = requestState.responseData.body;
    if (!cart || !Array.isArray(cart.items)) return;

    this.checkEligibility(cart);
  }

  initialCheck() {
    this.fetchCartAndCheck();
  }

  fetchCartAndCheck() {
    const root = window.Shopify?.routes?.root || '/';
    const token = this.mutationToken;
    fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((cart) =>
        this.whenEngineReady(() => {
          // A mutation started while this snapshot was in flight: drop it.
          if (token !== this.mutationToken) return;
          this.checkEligibility(cart);
        })
      )
      .catch(() => {});
  }

  whenEngineReady(fn, attempt = 0) {
    if (window.liquidAjaxCart) return fn();
    if (attempt > 20) return;
    setTimeout(() => this.whenEngineReady(fn, attempt + 1), 250);
  }

  checkEligibility(cart) {
    if (this.processing) return;
    // Never remove against a cart that is mid-mutation; the engine does not
    // serialize requests, and request-end will re-run this with fresh state.
    if (window.liquidAjaxCart?.processing) return;
    const items = cart.items || [];

    // Find the free gift item in the cart
    const giftItem = items.find(
      (item) => item.properties?.__wide_awake_gwp || item.properties?._wide_awake_gwp
    );

    // If no gift is in the cart, nothing to reconcile
    if (!giftItem) return;

    const eligibleIds = (this.dataset.eligibleProductIds || '')
      .split(',')
      .map(Number)
      .filter(Boolean);

    const minQty = parseInt(this.dataset.minQuantity, 10) || 2;

    // Count qualifying collection items (excluding the gift itself)
    // Option A (Total quantity): e.g. 2 of product A, or 1 of product A + 1 of product B
    const qualifyingCount = items.reduce((sum, item) => {
      if (item.key === giftItem.key) return sum;
      if (eligibleIds.includes(item.product_id)) {
        return sum + item.quantity;
      }
      return sum;
    }, 0);

    /* 
    // Option B (Distinct products): If you require 2 DIFFERENT products instead of quantity:
    // const qualifyingCount = items.filter(item => item.key !== giftItem.key && eligibleIds.includes(item.product_id)).length;
    */

    // If condition is broken, remove the gift
    if (qualifyingCount < minQty) {
      this.removeGift(giftItem);
    }
  }

  removeGift(giftItem) {
    this.processing = true;
    window.liquidAjaxCart.change(
      {
        id: giftItem.key,
        quantity: 0,
      },
      {
        info: { initiator: INITIATOR },
        lastCallback: () => {
          this.processing = false;
        },
      }
    );
  }
}

if (!customElements.get('cart-wide-awake-gwp')) {
  customElements.define('cart-wide-awake-gwp', CartWideAwakeGwp);
}